import { borrowerFirstNames, borrowerLastNames, lenderPortals, periods } from '../data/commissions';
import { Loan, Officer, Period, QualifyingLoan, Role } from '../types/commission';

/** Small deterministic PRNG so a user/period always renders the same production. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(value: string): number {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = (result << 5) - result + value.charCodeAt(index);
    result |= 0;
  }
  return Math.abs(result);
}

export function unitsFor(officer: Officer, role: Role, periodId: string): number {
  return officer.monthlyUnits?.[periodId] ?? role.monthlyUnits[periodId] ?? 0;
}

/** Builds the payment line items a user is paid on for a given period. */
export function buildLoans(period: Period, seedKey: string, count: number, allowSplits: boolean): Loan[] {
  const random = seededRandom(hash(`${seedKey}-${period.id}`));
  const [year, month] = period.id.split('-').map(Number);
  const lastDay = period.isCurrent ? period.daysElapsed : period.daysTotal;

  return Array.from({ length: count }, (_, index) => {
    const first = borrowerFirstNames[Math.floor(random() * borrowerFirstNames.length)];
    const last = borrowerLastNames[Math.floor(random() * borrowerLastNames.length)];
    const portal = lenderPortals[Math.floor(random() * lenderPortals.length)];
    const amount = Math.round((180000 + random() * 620000) / 100) * 100;
    const split = allowSplits && random() < 0.14;
    const reimbursement = random() < 0.08;
    const day = Math.max(1, Math.min(lastDay, Math.ceil(((index + 1) / count) * lastDay)));

    return {
      id: `LN-${String((hash(`${seedKey}${period.id}${index}`) % 90000) + 10000)}`,
      borrower: `${first} ${last}`,
      fundedDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      amount,
      lenderPortal: portal.name,
      lenderPortalUrl: portal.url,
      commissionPercentage: split ? 50 : 100,
      reimbursement,
    };
  }).sort((a, b) => a.fundedDate.localeCompare(b.fundedDate));
}

/** Loans counted toward a rolling-window promotion requirement, newest period first. */
export function buildQualifyingLoans(
  officer: Officer,
  role: Role,
  periodId: string,
  months: number,
): { loans: QualifyingLoan[]; windowPeriods: Period[] } {
  const startIndex = Math.max(0, periods.findIndex((period) => period.id === periodId));
  const windowPeriods = periods.slice(startIndex, startIndex + months);
  const loans = windowPeriods.flatMap((period) =>
    buildLoans(period, officer.id, unitsFor(officer, role, period.id), role.group === 'Production')
      .map((loan) => ({ ...loan, payPeriodName: period.label }))
      .reverse(),
  );
  return { loans, windowPeriods };
}
